import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type GitHubConnectionStatus = 'ACTIVE';

export interface GitHubConnection {
  status:       GitHubConnectionStatus;
  githubLogin:  string;
  githubUserId: number;
  scopes?:      string;
  connectedAt?: string;
  updatedAt?:   string;
}

export interface GitHubDefaults {
  configured: boolean;
  repository: string | null;
  updatedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class GitHubService {
  private readonly base = `${environment.apiUrl}/api/v1/integrations/github`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Starts the GitHub OAuth flow.
   * Returns the GitHub authorization URL; the caller should redirect the
   * browser to it (window.location.href = authUrl).
   */
  startConnect(): Observable<{ authUrl: string }> {
    return this.http.get<{ authUrl: string }>(`${this.base}/connect`);
  }

  /**
   * Returns the current user's GitHub connection, or null if none exists.
   */
  getConnection(): Observable<GitHubConnection | null> {
    return this.http.get<GitHubConnection>(`${this.base}/connection`).pipe(
      catchError(err => {
        if (err.status === 404) return of(null);
        throw err;
      }),
    );
  }

  /**
   * Removes the GitHub connection for the current user.
   * Does NOT revoke the GitHub OAuth token.
   */
  disconnect(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/connection`);
  }

  /**
   * Returns the current default repository config for GitHub Issues notifications.
   * Returns { configured: false, repository: null } when no default is set.
   */
  getDefaults(): Observable<GitHubDefaults> {
    return this.http.get<GitHubDefaults>(`${this.base}/defaults`);
  }

  /**
   * Saves the default repository for GitHub Issues notifications.
   * repository must be in "owner/repo" format.
   */
  saveDefaults(repository: string): Observable<GitHubDefaults> {
    return this.http.put<GitHubDefaults>(`${this.base}/defaults`, { repository });
  }
}
